import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { NOTIFICATION_DIALOG_ACTIONS } from './constants';
import { NotificationDialogContext } from './context';
import NotificationDialog from './Dialog';
import type {
  NotificationDialogProps,
  NotificationDialogResult,
} from './types';

type NotificationDialogState = NotificationDialogProps & { open: boolean };
type NotificationDialogProviderProps = { children: ReactNode };

export default function NotificationDialogProvider({
  children,
}: NotificationDialogProviderProps) {
  const resolvePromise = useRef<
    ((value: NotificationDialogResult) => void) | null
  >(null);

  const [state, setState] = useState<NotificationDialogState>({
    open: false,
  } as NotificationDialogState);

  const open = useCallback((props: NotificationDialogProps) => {
    setState({ ...props, open: true });
    return new Promise<NotificationDialogResult>((resolve) => {
      resolvePromise.current = resolve;
    });
  }, []);

  const close = useCallback(
    ({
      action = NOTIFICATION_DIALOG_ACTIONS.CLOSE,
    }: Partial<NotificationDialogResult> = {}) => {
      setState((prevState: NotificationDialogState) => ({
        ...prevState,
        open: false,
      }));
      resolvePromise.current?.({ action });
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    close({ action: NOTIFICATION_DIALOG_ACTIONS.CANCEL });
  }, [close]);

  const handleSubmitModal = useCallback(() => {
    close({ action: NOTIFICATION_DIALOG_ACTIONS.CONFIRM });
  }, [close]);

  const contextValue = useMemo(
    () => ({ open, close, NOTIFICATION_DIALOG_ACTIONS }),
    [open, close],
  );

  return (
    <NotificationDialogContext value={contextValue}>
      {children}

      <NotificationDialog
        {...state}
        disableEnforceFocus
        onClose={handleCloseModal}
        onConfirm={handleSubmitModal}
      />
    </NotificationDialogContext>
  );
}
